import { Button, LegacyStack, TextContainer } from '@shopify/polaris'
import React from 'react'
import { mutate } from 'swr'

import { ApiActionDialog } from '../common/ApiActionDialog'
import { ExternalLink, OrderLink, useDialogActivator } from '../common/helpers'
import { LegacyHelpIcon } from '../common/HelpIcon'

export interface PaymentDetailsProps {
  subscriptionRestId: number
  baId?: number
  status: string
  errorCode?: string
  errorMessage?: string
  orderGid?: string
  orderName?: string
  nextActionUrl?: string
}

const errorCodeMapping: Record<string, string> = {
  AMOUNT_TOO_LARGE: 'The amount exceeds the limit for this payment method.',
  AMOUNT_TOO_SMALL: 'The amount is too small.',
  AUTHENTICATION_ERROR: 'There was an error during the payment authentication.',
  AUTHENTICATION_FAILED: 'Authentication failed during payment processing.',
  AUTHENTICATION_REQUIRED: 'Additional authentication is required to complete the payment.',
  BUYER_CANCELED_PAYMENT_METHOD: 'Payment method was canceled by buyer.',
  CALL_ISSUER: 'The card issuer requires the cardholder to call them.',
  CANCELLED_PAYMENT: 'The payment was cancelled.',
  CARD_DECLINED: 'The card was declined by the payment processor.',
  CARD_NUMBER_INCORRECT: 'Card number was incorrect.',
  CONFIRMATION_REJECTED: 'The payment confirmation was rejected.',
  CUSTOMER_INVALID: 'Customer is invalid.',
  CUSTOMER_NOT_FOUND: 'Customer was not found.',
  DO_NOT_HONOR: "The card issuer declined the transaction with a 'do not honor' response.",
  EXPIRED_BUYER_ACTION: 'The required buyer action has expired.',
  EXPIRED_CARD: 'The card has expired.',
  EXPIRED_PAYMENT_METHOD: 'Payment method is expired.',
  FRAUD_SUSPECTED: 'Fraud was suspected.',
  FREE_GIFT_CARD_NOT_ALLOWED: 'Gift cards must have a price greater than zero.',
  GENERIC_ERROR: 'A generic payment error occurred.',
  INCORRECT_ADDRESS: 'The billing address provided is incorrect.',
  INCORRECT_NUMBER: 'The card number entered is incorrect.',
  INCORRECT_ZIP: 'The postal/ZIP code provided is incorrect.',
  INSUFFICIENT_FUNDS: 'Insufficient funds.',
  INSUFFICIENT_INVENTORY: 'Not enough inventory found.',
  INVALID_BILLING_ADDRESS: 'The billing address is invalid.',
  INVALID_CURRENCY: 'The currency is not supported for this payment method.',
  INVALID_CUSTOMER_BILLING_AGREEMENT:
    'The billing agreement or transaction ID reference is invalid.',
  INVALID_EXPIRY_DATE: 'The expiry date of the card provided is invalid.',
  INVALID_NUMBER: 'The card number is invalid.',
  INVALID_PAYMENT_METHOD:
    'Payment method is invalid. Please update or create a new payment method.',
  INVALID_PURCHASE_TYPE: 'The purchase type is invalid for this payment method.',
  INVALID_SHIPPING_ADDRESS: 'The shipping address is either missing or invalid.',
  INVENTORY_ALLOCATIONS_NOT_FOUND: 'No inventory location found or enabled.',
  INVOICE_ALREADY_PAID: 'A payment has already been made for this invoice.',
  MERCHANT_ACCOUNT_ERROR: 'Merchant account configuration error.',
  MERCHANT_RULE: 'The transaction was declined due to a merchant-defined rule.',
  NON_TEST_ORDER_LIMIT_REACHED: 'Test payment gateway required for additional orders.',
  OFF_SESSION_REJECTED: 'The off-session payment was rejected and requires customer action.',
  PAYMENT_METHOD_DECLINED: 'Payment method was declined by processor.',
  PAYMENT_METHOD_INCOMPATIBLE_WITH_GATEWAY_CONFIG:
    'Payment method cannot be used with the current payment gateway test mode configuration.',
  PAYMENT_METHOD_NOT_FOUND: 'Payment method was not found.',
  PAYMENT_METHOD_NOT_SPECIFIED: 'Payment method missing from subscription contract.',
  PAYMENT_METHOD_UNSUPPORTED: 'The payment method type is not supported.',
  PAYMENT_PROVIDER_ERROR: 'An exception occurred with the payment provider.',
  PAYMENT_PROVIDER_IS_NOT_ENABLED: 'Payment provider is not enabled.',
  PAYPAL_ERROR_GENERAL: 'Paypal Error General.',
  PROCESSING_ERROR: 'A processing error occurred with the payment provider.',
  PURCHASE_TYPE_NOT_SUPPORTED: 'Purchase Type is not supported.',
  RETRY_DECLINED: 'The payment retry was declined.',
  TEST_MODE: 'Gateway is in test mode and attempted to bill a live payment method.',
  TRANSACTION_LIMIT_EXCEEDED: "The transaction exceeds the cardholder's limit.",
  TRANSIENT_ERROR: 'Transient error, try again later.',
  UNEXPECTED_ERROR: 'There was an unexpected error during the billing attempt.',
}

export const PaymentDetails = (props: PaymentDetailsProps) => {
  const retryDialogActivator = useDialogActivator()
  const errorCodeDescription = props.errorCode ? errorCodeMapping[props.errorCode] ?? null : null

  if (props.errorMessage) {
    return (
      <p>
        {props.errorMessage}
        {errorCodeDescription ? <LegacyHelpIcon>{errorCodeDescription}</LegacyHelpIcon> : null} (
        <ExternalLink href="https://docs.paywhirl.com/en/articles/8602708-how-to-manage-failed-payments-and-declines-in-shopify">
          learn more
        </ExternalLink>
        )
      </p>
    )
  }

  if (props.orderGid && props.orderName) {
    return <OrderLink gid={props.orderGid}>Order {props.orderName}</OrderLink>
  }

  if (props.nextActionUrl) {
    return <ExternalLink href={props.nextActionUrl}>Requires confirmation</ExternalLink>
  }

  if (props.baId && props.status === 'Pending') {
    return (
      <>
        <Button onClick={retryDialogActivator.show} variant="plain">
          Retry
        </Button>
        <ApiActionDialog
          activator={retryDialogActivator}
          url={`/subscriptions/${props.subscriptionRestId}/retry-billing-attempt/${props.baId}`}
          method="POST"
          message={
            <LegacyStack>
              <TextContainer>
                <p>
                  The status of this payment is currently unknown. Do you want to retry? The
                  customer will NOT be charged twice.
                </p>
                <p>Note: Please refresh this page a few minutes after retry to check the status.</p>
              </TextContainer>
            </LegacyStack>
          }
          primaryButtonTitle="Retry"
          onSuccess={(res) =>
            mutate(['subscription-contract', `${props.subscriptionRestId}`], res.data, false)
          }
        />
      </>
    )
  }

  return null
}
